import React, { useState } from "react"
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    TextField,
    Button,
    Box,
    Typography,
    Divider,
    Paper,
    IconButton,
    CircularProgress,
    Chip,
    InputAdornment,
    Card,
    CardContent,
} from "@mui/material"
import {
    Close as CloseIcon,
    Add as AddIcon,
    LocalOffer as PriceIcon,
    Inventory as QuantityIcon,
    Percent as DiscountIcon,
    Receipt as ReceiptIcon,
    Info as InfoIcon,
    CalendarToday as CalendarIcon, PlaylistAdd,
} from "@mui/icons-material"
import { useTranslation } from "react-i18next"
import PropTypes from "prop-types"

const AddToListDialog = ({ open, onClose, onSubmit, product, loading = false }) => {
    const initialFormData = {
        quantity: "",
        price: "",
        discount: "",
        expiryDate: "",
    };
    const { t } = useTranslation()
    const [formData, setFormData] = useState(initialFormData)
    const [errors, setErrors] = useState({})

    const validateForm = () => {
        const newErrors = {}

        if (!formData.quantity || Number.parseInt(formData.quantity) <= 0) {
            newErrors.quantity = t("QuantityMsg")
        }
        if (!formData.price || Number.parseFloat(formData.price) <= 0) {
            newErrors.price = t("PriceMsg")
        }
        if (!formData.expiryDate) {
            newErrors.expiryDate = t("ExpireDateMsg")
        } else if (new Date(formData.expiryDate) < new Date()) {
            newErrors.expiryDate = t("ExpireDatePastMsg")
        }
        if (formData.discount && (Number.parseFloat(formData.discount) < 0 || Number.parseFloat(formData.discount) > 100)) {
            newErrors.discount = t("DiscountMsg")
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = () => {
        if (validateForm()) {
            const productData = {
                productId: product.id,
                productCode: product.productCode,
                productName: product.productName,
                quantity: Number.parseInt(formData.quantity),
                price: Number.parseFloat(formData.price),
                discount: formData.discount ? Number.parseFloat(formData.discount) : 0,
                expiryDate: formData.expiryDate || null,
            }
            onSubmit(productData)
            setFormData(initialFormData)
        }
    }

    const handleClose = () => {
        setFormData(initialFormData)
        setErrors({})
        onClose()
    }

    const handleInputChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }))
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: "" }))
        }
    }

    // Calculate totals
    const quantity = Number.parseInt(formData.quantity) || 0
    const price = Number.parseFloat(formData.price) || 0
    const discount = Number.parseFloat(formData.discount) || 0
    const subtotal = quantity * price
    const discountAmount = subtotal * (discount / 100)
    const total = subtotal - discountAmount

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                },
            }}
        >
            {/* Header */}
            <DialogTitle
                sx={{
                    backgroundColor: "primary.main",
                    color: "white",
                    position: "relative",
                    py: 3,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <PlaylistAdd sx={{ fontSize: 28 }} />
                    <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
                        {t("AddToMyProducts")}
                    </Typography>
                </Box>
                <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
                    {t("AddToListDesc")}
                </Typography>
                <IconButton
                    onClick={handleClose}
                    sx={{
                        position: "absolute",
                        right: 8,
                        top: 8,
                        color: "white",
                        "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.1)",
                        },
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ p: 3 }}>
                    {product && (
                        <Card
                            elevation={0}
                            sx={{
                                mb: 2,
                                backgroundColor: "#f0f9ff",
                                border: "1px solid #bae6fd",
                                borderRadius: 2,
                            }}
                        >
                            <CardContent sx={{ p: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                    <InfoIcon sx={{ color: "#0284c7", fontSize: 20 }} />
                                    <Typography variant="subtitle1" sx={{ color: "#0284c7", fontWeight: 600 }}>
                                        {t("ProductDetails")}
                                    </Typography>
                                </Box>
                                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, fontSize: "1.1rem" }}>
                                    {product.productName}
                                </Typography>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
                                    <Chip label={`${t("Code")}: ${product.productCode}`} size="small" variant="outlined" />
                                    <Chip label={`${t("Category")}: ${t(product.productCategory)}`} size="small" variant="outlined" color="primary" />
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem" }}>
                                    <strong>{t("Manufacturer")}:</strong> {product.productManufacturer} | <strong>{t("Country")}:</strong> {product.productCountry}
                                </Typography>
                            </CardContent>
                        </Card>
                    )}

                    {/* All Fields in One Section */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            backgroundColor: "#fefefe",
                            border: "1px solid #e0e0e0",
                            borderRadius: 2,
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 2,
                                color: "#1976d2",
                                fontWeight: 600,
                                fontSize: "1.1rem",
                            }}
                        >
                            <PriceIcon />
                            {t("MyProductInformation")}
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label={`${t("Quantity")} *`}
                                    type="number"
                                    size="small"
                                    value={formData.quantity}
                                    onChange={(e) => handleInputChange("quantity", e.target.value)}
                                    error={!!errors.quantity}
                                    helperText={errors.quantity}
                                    variant="outlined"
                                    inputProps={{ min: 1 }}
                                    InputProps={{
                                        startAdornment: <QuantityIcon sx={{ mr: 1, color: "#6b7280", fontSize: 20 }} />,
                                    }}
                                    placeholder="e.g., 100"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label={`${t("UnitPrice")} *`}
                                    type="number"
                                    size="small"
                                    value={formData.price}
                                    onChange={(e) => handleInputChange("price", e.target.value)}
                                    error={!!errors.price}
                                    helperText={errors.price}
                                    variant="outlined"
                                    inputProps={{ min: 0, step: 0.01 }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <PriceIcon sx={{ color: "#6b7280", fontSize: 20 }} />
                                            </InputAdornment>
                                        ),
                                    }}
                                    placeholder="0.00"
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label={`${t("ExpiryDate")} *`}
                                    type="date"
                                    size="small"
                                    value={formData.expiryDate || ""}
                                    onChange={(e) => handleInputChange("expiryDate", e.target.value)}
                                    error={!!errors.expiryDate}
                                    helperText={errors.expiryDate}
                                    variant="outlined"
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    inputProps={{
                                        min: new Date().toISOString().split("T")[0],
                                    }}
                                    InputProps={{
                                        startAdornment: <CalendarIcon sx={{ mr: 1, color: "#6b7280", fontSize: 20 }} />,
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label={`${t("Discount")} (${t("Optional")})`}
                                    type="number"
                                    size="small"
                                    value={formData.discount}
                                    onChange={(e) => handleInputChange("discount", e.target.value)}
                                    error={!!errors.discount}
                                    helperText={errors.discount || "0-100%"}
                                    variant="outlined"
                                    inputProps={{ min: 0, max: 100, step: 0.01 }}
                                    InputProps={{
                                        startAdornment: <DiscountIcon sx={{ mr: 1, color: "#6b7280", fontSize: 20 }} />,
                                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                                    }}
                                    placeholder="0"
                                />
                            </Grid>
                        </Grid>
                    </Paper>

                    {/*{(formData.quantity || formData.price) && (*/}
                    {/*    <Paper*/}
                    {/*        elevation={0}*/}
                    {/*        sx={{*/}
                    {/*            p: 2,*/}
                    {/*            mt: 2,*/}
                    {/*            backgroundColor: "#f8f9fa",*/}
                    {/*            border: "1px solid #dee2e6",*/}
                    {/*            borderRadius: 2,*/}
                    {/*        }}*/}
                    {/*    >*/}
                    {/*        <Typography*/}
                    {/*            variant="subtitle1"*/}
                    {/*            sx={{*/}
                    {/*                display: "flex",*/}
                    {/*                alignItems: "center",*/}
                    {/*                gap: 1,*/}
                    {/*                mb: 1,*/}
                    {/*                color: "#495057",*/}
                    {/*                fontWeight: 600,*/}
                    {/*            }}*/}
                    {/*        >*/}
                    {/*            <ReceiptIcon sx={{ fontSize: 20 }} />*/}
                    {/*            {t("Summary")}*/}
                    {/*        </Typography>*/}
                    {/*        <Grid container spacing={1}>*/}
                    {/*            <Grid item xs={6}>*/}
                    {/*                <Typography variant="body2">*/}
                    {/*                    {t("Quantity")}: <strong>{quantity} {t("Units")}</strong>*/}
                    {/*                </Typography>*/}
                    {/*            </Grid>*/}
                    {/*            <Grid item xs={6}>*/}
                    {/*                <Typography variant="body2">*/}
                    {/*                    {t("UnitPrice")}: <strong>${price.toFixed(2)}</strong>*/}
                    {/*                </Typography>*/}
                    {/*            </Grid>*/}
                    {/*            <Grid item xs={6}>*/}
                    {/*                <Typography variant="body2">*/}
                    {/*                    {t("Subtotal")}: <strong>${subtotal.toFixed(2)}</strong>*/}
                    {/*                </Typography>*/}
                    {/*            </Grid>*/}
                    {/*            <Grid item xs={6}>*/}
                    {/*                {discount > 0 ? (*/}
                    {/*                    <Typography variant="body2" color="success.main">*/}
                    {/*                        {t("Discount")}: <strong>-${discountAmount.toFixed(2)}</strong>*/}
                    {/*                    </Typography>*/}
                    {/*                ) : (*/}
                    {/*                    <Typography variant="body2">{t("NoDiscount")}</Typography>*/}
                    {/*                )}*/}
                    {/*            </Grid>*/}
                    {/*            {formData.expiryDate && (*/}
                    {/*                <Grid item xs={12}>*/}
                    {/*                    <Typography variant="body2">*/}
                    {/*                        {t("Expiry")}: <strong>{new Date(formData.expiryDate).toLocaleDateString()}</strong>*/}
                    {/*                    </Typography>*/}
                    {/*                </Grid>*/}
                    {/*            )}*/}
                    {/*            <Grid item xs={12}>*/}
                    {/*                <Divider sx={{ my: 0.5 }} />*/}
                    {/*                <Typography variant="h6" fontWeight="bold" color="primary.main" textAlign="center">*/}
                    {/*                    {t("Total")}: ${total.toFixed(2)}*/}
                    {/*                </Typography>*/}
                    {/*            </Grid>*/}
                    {/*        </Grid>*/}
                    {/*    </Paper>*/}
                    {/*)}*/}
                </Box>
            </DialogContent>

            <Divider />

            <DialogActions sx={{ p: 3, gap: 2 }}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    size="large"
                    sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                    }}
                >
                    {t("Cancel")}
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    size="large"
                    disabled={loading || !formData.quantity || !formData.price || !formData.expiryDate}
                    startIcon={loading ? <CircularProgress size={16} /> : <AddIcon />}
                    sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                        backgroundColor: "primary.main",
                        "&:hover": {
                            backgroundColor: "primary.dark",
                        },
                    }}
                >
                    {loading ? t("AddingToList") : t("AddToMyProducts")}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

AddToListDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    product: PropTypes.object,
    loading: PropTypes.bool,
}

export default AddToListDialog
